<template>
  <AppContainer max-width="max-w-3xl">
    <form
      autocomplete="off"
      @submit.prevent="submitForm"
    >
      <AppCard>
        <div>
          <div>
            <AppGrid
              cols="2"
            >
              <AppGridItem>
                <TextInput
                  v-model.trim="formData.firstName"
                  label="First Name"
                  :class="{ 'input-error': hasValidationError('firstName') }"
                  :validation-error="hasValidationError('firstName') ? getValidationError('firstName') : null"
                  required
                  focus
                />
              </AppGridItem>

              <AppGridItem>
                <TextInput
                  v-model.trim="formData.lastName"
                  label="Last Name"
                  :class="{ 'input-error': hasValidationError('lastName') }"
                  :validation-error="hasValidationError('lastName') ? getValidationError('lastName') : null"
                  required
                />
              </AppGridItem>

              <AppGridItem col-span="2">
                <TextInput
                  v-model.trim="formData.email"
                  label="Email"
                  :class="{ 'input-error': hasValidationError('email') }"
                  class="w-1/2"
                  :validation-error="hasValidationError('email') ? getValidationError('email') : null"
                  required
                  type="email"
                />
              </AppGridItem>

              <AppGridItem
                col-span="2"
                class="input-field mb-1"
              >
                <label for="roles">
                  Roles
                </label>

                <Multiselect
                  v-model="formData.roles"
                  :options="Object.values(Role)"
                  :loading="loading"
                  multiple
                  :limit="3"
                  :limit-text="count => `+ ${count} more`"
                  :close-on-select="false"
                  :show-labels="false"
                  placeholder="Select roles"
                  :searchable="false"
                  class="user-form-multiselect"
                />

                <span
                  v-if="hasValidationError('roles')"
                  class="text-red-500"
                >
                  {{ getValidationError('roles') }}
                </span>
              </AppGridItem>

              <AppGridItem
                col-span="2"
                class="input-field"
              >
                <label for="districts">
                  Districts
                </label>

                <Multiselect
                  v-model="selectedDistricts"
                  :options="districts"
                  track-by="id"
                  label="name"
                  :loading="loading"
                  multiple
                  :limit="2"
                  :limit-text="count => `+ ${count} more`"
                  :close-on-select="false"
                  :show-labels="false"
                  placeholder="Select a district"
                  :searchable="false"
                  class="user-form-multiselect"
                />

                <span
                  v-if="hasValidationError('districts')"
                  class="text-red-500"
                >
                  {{ getValidationError('districts') }}
                </span>
              </AppGridItem>
            </AppGrid>
          </div>
        </div>

        <template #buttons>
          <ButtonSubmitRow
            :cancel-route="{ name: 'app_user_list' }"
            :processing="isSubmitting"
          />
        </template>
      </AppCard>
    </form>
  </AppContainer>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref, watch } from 'vue';
import Multiselect from 'vue-multiselect';
import { useRouter } from 'vue-router';

import { AppContainer, AppGrid, AppGridItem } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { AppCard } from '@/components/cards';
import { TextInput } from '@/components/form-inputs';

import { useFormSubmit, useHttp } from '@/composables';
import { Role } from '@/const';

const props = defineProps({
  model: {
    type: Object,
    default: () => null,
  },
});

const { submit, hasValidationError, getValidationError, isSubmitting } = useFormSubmit();
const router = useRouter();

const loading = ref(true);

const districts = ref([]);
const formData = reactive({
  firstName: props.model?.firstName || null,
  lastName: props.model?.lastName || null,
  email: props.model?.email || null,
  roles: props.model?.roles || [],
  districts: props.model?.districts.map(district => district.id) || [],
});

let selectedDistricts = ref(props.model?.districts || []);

const loadDistricts = async () => {
  const response = await useHttp().get('/util/districts');

  if (response.statusCode === HttpStatusCode.Ok) {
    districts.value = response.data.items;
  }

  loading.value = false;
};

loadDistricts();

const submitForm = async () => {
  const response = await submit(props.model?.id, formData, 'app_user_list', 'users');

  if (response.statusCode === HttpStatusCode.Ok) {
    await router.push({ name: 'app_user_list' });
  }
};

watch(selectedDistricts, () => {
  formData.districts = selectedDistricts.value.map(district => district.id);
});
</script>
